import React from "react";
import {
  useParams,
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Paper,
} from "@mui/material";
import { BrandContext } from "../../App";
import { useTheme } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";

import Moment from "moment";

import ReactPlayer from "react-player";
import { CallScreen } from "../../components/complex/CallScreen";
import DefaultBadge from "../../components/simple/DefaultBadge";
import DefaultButton from "../../components/simple/DefaultButton";
import {
  ArrowForwardRounded,
  BarChart,
  VolumeUpRounded,
} from "@mui/icons-material";
import PoweredBy from "../../assets/logos/powered-by.png";

import VideoAvatar from "../../components/VideoAvatar/VideoAvatar";

import { useAPI } from "../../hooks/useAPI";

import { useAuth } from "../../contexts/AuthContext";

export function ExternalScenario() {
  const { brand, user } = React.useContext(BrandContext);
  const { authToken } = useAuth();
  const { callAPI } = useAPI();
  const [simulationId, setSimulationId] = React.useState(null);
  const [scenarioData, setScenarioData] = React.useState(null);
  const [simulationData, setSimulationData] = React.useState(null);
  const [simulationHistory, setSimulationHistory] = React.useState(null);
  const [simulationLineChartData, setSimulationLineChartData] =
    React.useState(null);
  const [simulationCallURL, setSimulationCallURL] = React.useState("");
  const [simulationCallToken, setSimulationCallToken] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [popupBlocked, setPopupBlocked] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [showCallScreen, setShowCallScreen] = React.useState(true);
  const [isAudio, setIsAudio] = React.useState(false);
  const theme = useTheme();

  const [integrationType, setIntegrationType] = React.useState("unity");
  const [simStarted, setSimStarted] = React.useState(false);

  const [searchParams] = useSearchParams();

  const { scenario_id } = useParams();
  const navigate = useNavigate();

  Moment.locale("en");

  function startScenario({ audio }) {
    setSimStarted(true);
    setLoading(true);
    setIsAudio(audio);
    console.log("ExternalScenario -- Starting scenario with ID", scenario_id);
    callAPI("start_simulation_external", {
      method: "POST",
      body: { scenario_id: scenario_id },
    })
      .then((data) => {
        if (data["id"] && data["livekit_url"]) {
          setSimulationCallURL(data["livekit_url"]);
          setSimulationId(data["id"]);
          setSimulationCallToken(data["token"]);
          setSimulationData(data);
          setLoading(false);
        } else {
          setSnackbarMessage(
            data["livekit_url"]
              ? "Starting simulation failed"
              : "No LiveKit URL available"
          );
          setLoading(false);
        }
        console.log("/start_simulation_external", data);
      })
      .catch((err) => {
        setSnackbarMessage("Starting simulation failed");
        console.log(err.message);
        setLoading(false);
        setSimStarted(false);
      });
  }

  React.useEffect(() => {
    document.title = `${
      scenarioData?.name ? scenarioData.name : "GuruNow Simulation"
    }`;
  }, [scenarioData]);

  React.useEffect(() => {
    callAPI(`external_scenario?scenario_id=${scenario_id}`)
      .then((data) => {
        setScenarioData(data);
      })
      .catch((err) => {
        console.log(err.message);
      });
  }, [authToken, scenario_id]);

  function endSimulation() {
    setShowCallScreen(false);
    callAPI("rtc_end_external_simulation", {
      method: "POST",
      body: { simulation_id: simulationId },
    })
      .then((data) => {
        if (scenarioData.has_scoring_engine) {
          window.location.href = "/externalscenario_feedback/" + simulationId;
        } else {
          window.location.href = "/externalscenario/" + scenarioData.id;
        }
      })
      .catch((err) => {
        setSnackbarMessage("Ending simulation failed");
        console.log(err.message);
        setLoading(false);
      });
  }

  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  React.useEffect(() => {
    if (simulationData && simulationData?.type)
      setIntegrationType(simulationData.type);
  }, [simulationData?.type]);

  return (
    <Box sx={{ backgroundColor: theme.palette.background.primary }}>
      <Snackbar
        open={snackbarMessage ? true : false}
        autoHideDuration={5000}
        onClose={() => {
          setSnackbarMessage("");
        }}
        message={snackbarMessage}
      />
      {scenarioData != null && (
        <>
          <Box
            sx={{
              height: "100vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {simulationId == null ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    backgroundColor: "transparent",
                    boxShadow:
                      "0px 12px 16px -4px rgba(154, 152, 191, 0.08), 0px 4px 6px -2px rgba(255, 255, 255, 0.03), 0px 2px 2px -1px rgba(255, 255, 255, 0.04)",
                    border: `2px solid ${theme.palette.border.tertiary}`,
                    borderRadius: theme.shape.radius,
                    padding: theme.containers.paddingDesktop,
                    margin: "0 20%",
                  }}
                >
                  {scenarioData.briefing_video ? (
                    <>
                      <ReactPlayer
                        src={scenarioData.briefing_video}
                        controls={true}
                        width="100%"
                        height="auto"
                      />
                    </>
                  ) : null}
                  {searchParams.get("blocked") === "true" && (
                    <Alert severity="error" sx={{ m: 2, p: 2 }}>
                      Please allow popup windows to run this simulation.
                    </Alert>
                  )}
                  <Typography
                    sx={{
                      fontSize: theme.typography.displayXS.fontSize,
                      fontWeight: theme.typography.fontWeightMedium,
                      lineHeight: theme.typography.displayXS.lineHeight,
                      mt: scenarioData.briefing_video
                        ? theme.customSpacing["2xl"]
                        : null,
                      mb: theme.customSpacing.xs,
                    }}
                  >
                    {scenarioData.name}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: theme.customSpacing.xl,
                    }}
                  >
                    {scenarioData.author === "Unknown author" ? null : (
                      <Typography
                        sx={{
                          fontSize: theme.typography.textXS.fontSize,
                          fontWeight: theme.typography.fontWeightLight,
                        }}
                      >
                        By {scenarioData.author}
                      </Typography>
                    )}
                    {/* <DefaultBadge
                                            variant='pillTransparent'
                                            children={'Beginner'}
                                            size='small'
                                            startIcon={<BarChart sx={{ fontSize: theme.typography.textXS.fontSize, color: theme.palette.foreground.badge_icon }} />}
                                        /> */}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: theme.customSpacing["2xl"],
                    }}
                  >
                    <Tabs
                      textColor={theme.palette.text.button_hover}
                      value={value}
                      onChange={handleChange}
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        padding: 0,
                        gap: theme.customSpacing["2xl"],
                        "& .MuiTab-root": {
                          color: theme.palette.text.primary,
                          fontWeight: theme.typography.fontWeightRegular,
                          textTransform: "none",
                          textDecoration: "none",
                        },
                        "& .Mui-selected": {
                          color: theme.palette.text.button_primary_hover,
                          fontWeight: theme.typography.fontWeightBold,
                        },
                        "& .MuiTabs-indicator": {
                          backgroundColor:
                            theme.palette.background.button_primary_hover,
                        },
                      }}
                    >
                      <Tab label="Overview" />
                    </Tabs>
                    <Box>
                      <DefaultButton
                        onClick={() => startScenario({ audio: false })}
                        variant="contained"
                        disabled={simStarted}
                        size="sm"
                        sx={{ m: 2 }}
                        endIcon={
                          !simStarted ? (
                            <ArrowForwardRounded />
                          ) : (
                            <CircularProgress size="18px" />
                          )
                        }
                      >
                        {simStarted ? (
                          <>Simulation Starting...</>
                        ) : (
                          <>Start Simulation</>
                        )}
                      </DefaultButton>
                      {/* <DefaultButton onClick={() => startScenario({ audio: true })} variant="contained" size="sm" sx={{ backgroundColor: theme.palette.background.button_primary_hover, color: 'white' }} startIcon={<VolumeUpRounded />}>
                                                Audio Only Call
                                            </DefaultButton> */}
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: theme.typography.textMD.fontSize,
                      color: theme.palette.text.gray,
                      fontWeight: theme.typography.fontWeightLight,
                    }}
                  >
                    {scenarioData.long_description}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                {showCallScreen ? (
                  <>
                    {integrationType === "video" ? (
                      <VideoAvatar
                        config={simulationData || {}}
                        scenario={scenarioData}
                      />
                    ) : (
                      <CallScreen
                        url={simulationCallURL}
                        //onDisconnect={endSimulation}
                        scenarioData={{
                          ...scenarioData,
                          simulationId: simulationId,
                          external_scenario: true,
                          livekit_url: simulationCallURL,
                          token: simulationCallToken, // pass the token
                          useProvidedToken: true,
                        }}
                        simulationData={simulationData}
                        isAudio={isAudio}
                      />
                    )}
                  </>
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => setShowCallScreen(true)}
                  >
                    Start Call
                  </Button>
                )}
              </>
            )}
          </Box>
        </>
      )}
      <Box
        sx={{
          position: "absolute",
          bottom: theme.customSpacing["2xl"],
          right: theme.customSpacing["2xl"],
          width: 85,
        }}
      >
        <img
          src={PoweredBy}
          alt="Powered by"
          style={{ width: "100%", height: "auto" }}
        />
      </Box>
    </Box>
  );
}
